import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { Link, useLocation } from 'react-router-dom'
import { Dropdown, DropdownMenu, DropdownToggle, Form, Input } from 'reactstrap'
import auth from '../api/auth/hooks'
import { default as logoLight, default as logoSm } from '../assets/images/dimedia-crm-logo-alt.png'
import { DETAIL_SEARCH_TYPE } from '../common/constants'
import FullScreenDropdown from '../Components/Common/FullScreenDropdown'
import LanguageDropdown from '../Components/Common/LanguageDropdown/LanguageDropdown'
import ProfileDropdown from '../Components/Common/ProfileDropdown'
import QuickSearchInput from '../Components/Common/QuickSearchInput/QuickSearchInput'
import DetailSearchButton from '../Components/DetailSearch/components/DetailSearchButton'
import NotificationDropdown from '../Components/NotificationDropdown/NotificationDropdown'
import { selectIsImpersonated } from '../store/auth'
import { clearDetailSearchCriteria, selectDetailSearchCriteria } from '../store/search'
import HamburgerButton from './HamburgerButton'

const detailSearchPathTypes = {
    properties: DETAIL_SEARCH_TYPE.PROPERTY,
    projects: DETAIL_SEARCH_TYPE.PROJECT,
    contacts: DETAIL_SEARCH_TYPE.CONTACT,
    companies: DETAIL_SEARCH_TYPE.COMPANY,
    documents: DETAIL_SEARCH_TYPE.DOCUMENT,
    'intermediary-diary': DETAIL_SEARCH_TYPE.INTERMEDIARY_DIARY,
    'evidence-diary': DETAIL_SEARCH_TYPE.EVIDENCE_DIARY,
}
const detailSearchPaths = Object.keys(detailSearchPathTypes)

// Quick search only covers project/property/contact/company (see QuickSearchInput.js)
const quickSearchPaths = ['projects', 'properties', 'contacts', 'companies']

const Header = ({ btnFunction }) => {
    const { t } = useTranslation()
    const dispatch = useDispatch()
    const location = useLocation()

    const [search, setSearch] = useState(false)
    const [mobileSearchValue, setMobileSearchValue] = useState('')

    const isImpersonated = useSelector(selectIsImpersonated)
    const detailSearchCriteria = useSelector(selectDetailSearchCriteria)

    const { data: agentHomepage } = auth.CurrentAgent.useQuery({ select: (data) => data.agentHomepage })

    const supportsQuickSearch = quickSearchPaths.some((path) => location.pathname.includes(path))

    // Effect to clear outdated detail search criteria when navigating to a different module page
    useEffect(() => {
        const path = location.pathname.split('/')[1]
        const currentType = detailSearchPathTypes[path]

        if (detailSearchCriteria?.searchType && detailSearchCriteria.searchType !== currentType) {
            dispatch(clearDetailSearchCriteria(detailSearchCriteria.searchType))
        }
    }, [location.pathname, detailSearchCriteria, dispatch])

    const toogleSearch = () => {
        setSearch(!search)
    }

    return (
        <>
            <header id="page-topbar">
                <div className="layout-width">
                    <div className="navbar-header">
                        <div className="d-flex aling-items-center">
                            <div className="navbar-brand-box horizontal-logo">
                                <Link to={agentHomepage || '/properties'} className="logo logo-light d-flex align-items-center">
                                    <span className="logo-sm">
                                        <img src={logoSm} alt="" height="22" />
                                    </span>
                                    <span className="logo-lg">
                                        <img src={logoLight} alt="" height="36" />
                                    </span>
                                    {isImpersonated && <span className="badge badge-soft-warning ms-2 fs-12">Impersonation</span>}
                                </Link>
                            </div>

                            <HamburgerButton btnFunction={btnFunction} />

                            {/* Desktop quick search */}
                            {supportsQuickSearch && <QuickSearchInput />}

                            {/* Detail search - shown on supported module pages */}
                            {location?.pathname &&
                                (location.pathname === '/' || detailSearchPaths.some((path) => location.pathname.includes(path))) && (
                                    <DetailSearchButton />
                                )}
                        </div>

                        <div className="d-flex align-items-center">
                            {/* Mobile quick search */}
                            {supportsQuickSearch && (
                                <Dropdown isOpen={search} toggle={toogleSearch} className="d-lg-none topbar-head-dropdown header-item">
                                    <DropdownToggle type="button" tag="button" className="btn btn-icon btn-topbar btn-ghost-secondary rounded-circle">
                                        <i className="bx bx-search fs-22"></i>
                                    </DropdownToggle>
                                    <DropdownMenu className="p-3" style={{ minWidth: '100vw', maxWidth: '100vw' }}>
                                        <Form>
                                            <div className="form-group">
                                                <div className="input-group position-relative">
                                                    <Input
                                                        type="text"
                                                        className="form-control"
                                                        placeholder={`${t('quickSearch.placeholder')}...`}
                                                        aria-label="Search"
                                                        value={mobileSearchValue}
                                                        onChange={(e) => setMobileSearchValue(e.target.value)}
                                                    />
                                                    {mobileSearchValue && (
                                                        <span
                                                            className="mdi mdi-close-circle position-absolute top-50 translate-middle-y"
                                                            style={{ right: '20px', cursor: 'pointer', fontSize: '18px', zIndex: 10 }}
                                                            onClick={() => setMobileSearchValue('')}
                                                        ></span>
                                                    )}
                                                </div>
                                            </div>
                                        </Form>
                                        <QuickSearchInput
                                            hideDesktopInput={true}
                                            mobileSearchValue={mobileSearchValue}
                                            setMobileSearchValue={setMobileSearchValue}
                                            mobileSearchClose={() => setSearch(false)}
                                        />
                                    </DropdownMenu>
                                </Dropdown>
                            )}

                            <FullScreenDropdown />

                            <LanguageDropdown />

                            <NotificationDropdown />

                            <ProfileDropdown />
                        </div>
                    </div>
                </div>
            </header>
        </>
    )
}

export default Header
